/**
 * The Morning Nob's dry vocabulary (M16, FR-7.3). Pure copy and pure
 * derivations — how the receipts line reads, how a sender's line under their
 * name is written, how a cadence and an hour are spoken — so every judgement is
 * testable on its own and the briefing itself stays a render of what it holds.
 */
import type { RecapReceipts, RecapSender } from "../../lib/api";
import { domainOf } from "../inbox/labels";

/**
 * The receipts line for a recap item — the dry evidence, in the same grammar as
 * the inbox: "✓ 4 of 4 claims · 14 pages · 7 sources". The ✓ is typography, not
 * a pictograph.
 */
export function recapReceiptsLine(r: RecapReceipts): string {
  const claims = r.claims_verified + r.claims_refuted;
  return [
    `✓ ${r.claims_verified} of ${claims} claims`,
    `${r.pages_reviewed} pages`,
    `${r.sources_checked} sources`,
  ].join(" · ");
}

/**
 * The line under a sender's name — "Founder, tandemhire.com" — from the company
 * they gave. `null` when there is no company URL: an assumption we do not have
 * the grounds to make is one we do not make.
 */
export function founderLine(sender: RecapSender): string | null {
  const domain = domainOf(sender.company_url);
  return domain ? `Founder, ${domain}` : null;
}

/** The cadences the recipient can set from the recap (FR-7.1). */
export const CADENCES = ["daily", "weekly"] as const;

/**
 * `weekly` says WHICH DAY (ruling R-0910-3): a weekly digest goes out on Monday,
 * and a button that only said "Weekly" left the owner to find that out by
 * waiting a week.
 */
export const CADENCE_LABEL: Record<string, string> = {
  daily: "Daily",
  weekdays: "Weekdays",
  weekly: "Weekly, Mondays",
  off: "Off",
};

/** What REALTIME reads as on the settings plate: alerts, and no digest at all. */
export const ALERTS_EVERY_LINE = "Alerts as they happen, no digest";

/** A cadence read plainly, falling back to the raw word rather than blanking. */
export function cadenceLabel(cadence: string): string {
  return CADENCE_LABEL[cadence] ?? cadence;
}

/** The delivery hour as a time of day — "08:00", not "8". A recap is a moment. */
export function hourLabel(hour: number): string {
  const h = Number.isFinite(hour) ? Math.max(0, Math.min(23, Math.round(hour))) : 8;
  return `${String(h).padStart(2, "0")}:00`;
}

/** The position badge, two digits, so the five read as a column: 01, 02, … */
export function positionLabel(position: number): string {
  const n = Number.isFinite(position) ? Math.max(0, Math.round(position)) : 0;
  return String(n).padStart(2, "0");
}
